import type { CentrixSchemas, PitstopRoutes } from "core/entities/APITypes";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";

type FeatureFlagsDto = CentrixSchemas["Fixhub.Settings.PitstopFeatureFlagsDto"];
type FeatureFlagKeys = keyof FeatureFlagsDto;

const featureFlagPathMapping: Partial<Record<FeatureFlagKeys, PitstopRoutes>> =
  {
    financialPageActive: "/[region]/financial",
    interactiveMapPageActive: "/[region]/interactive-map",
  };
const pathFeatureFlagMapping: Record<PitstopRoutes, FeatureFlagKeys> =
  Object.entries(featureFlagPathMapping).reduce(
    (acc, [flagKey, path]) => {
      acc[path as PitstopRoutes] = flagKey as FeatureFlagKeys;
      return acc;
    },
    {} as Record<PitstopRoutes, FeatureFlagKeys>
  );

export type FeatureFlags = Record<FeatureFlagKeys, boolean>;

export const defaultFeatureFlags: FeatureFlags = {
  financialPageActive: false,
  interactiveMapPageActive: false,
  interactiveMapWidgetActive: false,
  sociReviewsActive: false,
};

export function useFeatureFlags(): FeatureFlags {
  const brcyCode = useBrcyCode();
  const { data, isLoading, isError } = useCentrixApi({
    path: "/api/app/pitstop-settings/feature-flags",
    parameters: { query: { brcyCode: brcyCode } },
  });
  if (isLoading || isError || !data) {
    return defaultFeatureFlags;
  }
  return {
    financialPageActive: data.financialPageActive ?? false,
    interactiveMapPageActive: data.interactiveMapPageActive ?? false,
    interactiveMapWidgetActive: data.interactiveMapWidgetActive ?? false,
    sociReviewsActive: data.sociReviewsActive ?? false,
  };
}

export function useIsFeatureFlagEnabled(
  featureFlagKey: FeatureFlagKeys
): boolean {
  const featureFlags = useFeatureFlags();
  return featureFlags[featureFlagKey];
}

type FeatureFlagByRouteResult =
  | {
      isFeatureFlagRoute: true;
      isEnabled: boolean;
      featureFlagKey: FeatureFlagKeys;
    }
  | {
      isFeatureFlagRoute: false;
      isEnabled: boolean;
    };
export function useFeatureFlagByRoute(
  route: PitstopRoutes
): FeatureFlagByRouteResult {
  const featureFlags = useFeatureFlags();
  const featureFlagKey = pathFeatureFlagMapping[route];
  if (!featureFlagKey) {
    return { isFeatureFlagRoute: false, isEnabled: true };
  }
  return {
    isFeatureFlagRoute: true,
    isEnabled: featureFlags[featureFlagKey],
    featureFlagKey,
  };
}
